import type { SelectChangeEvent } from '@mui/material';

import React from 'react';

import {
  Box,
  Select,
  Button,
  Popover,
  MenuItem,
  useTheme,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  useMediaQuery,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useGetLabels } from 'src/pages/contacts/hooks/useGetLabels';
import { getContrastYIQ } from 'src/pages/chat/components/shared/misc/chat-label';

import { Iconify } from '../iconify';

interface Filter {
  column: string;
  operator: string;
  value: string;
}

interface FilterPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  advanceFilterFields?: any;
  setAdvanceFilter?: any;
  advanceFilters?: any;
  resetPagination?: any;
  columns?: any[];
  customFields?: any[];
}

const operators = [
  'contains',
  'does not contain',
  'equals',
  'does not equal',
  'starts with',
  'ends with',
  'is empty',
  'is not empty',
];

const getFieldLabel = (col: string, t: any, columns?: any[], customFields?: any[]) => {
  const labelMap: Record<string, string> = {
    ticketNo: t('TICKET_NO') !== 'TICKET_NO' ? t('TICKET_NO') : 'Ticket No',
    createdAt: t('CREATED_DATE') !== 'CREATED_DATE' ? t('CREATED_DATE') : 'Created Date',
    updatedAt: t('UPDATED_DATE') !== 'UPDATED_DATE' ? t('UPDATED_DATE') : 'Updated Date',
    status: t('STATUS') !== 'STATUS' ? t('STATUS') : 'Status',
    priority: t('PRIORITY') !== 'PRIORITY' ? t('PRIORITY') : 'Priority',
    title: t('TITLE') !== 'TITLE' ? t('TITLE') : 'Title',
    channelName: t('CHANNEL_NAME') !== 'CHANNEL_NAME' ? t('CHANNEL_NAME') : 'Channel Name',
    performedBy: t('PERFORMED_BY') !== 'PERFORMED_BY' ? t('PERFORMED_BY') : 'Performed By',
  };

  if (labelMap[col]) {
    return labelMap[col];
  }

  // Check columns mapping
  if (columns && columns.length > 0) {
    const foundCol = columns.find(
      (c: any) =>
        c.id?.toLowerCase() === col?.toLowerCase() ||
        c.label?.toLowerCase() === col?.toLowerCase()
    );
    if (foundCol && foundCol.label) {
      return foundCol.label;
    }
  }

  // Check customFields mapping
  if (customFields && customFields.length > 0) {
    const foundCf = customFields.find((cf: any) => {
      const cfId = String(cf.id).toLowerCase();
      const cfName = String(cf.name || '').toLowerCase();
      const colLower = col.toLowerCase();
      return (
        colLower === cfId ||
        colLower === cfName ||
        colLower === `cf_${cfId}` ||
        colLower === `cf_${cfName}`
      );
    });
    if (foundCf && foundCf.label) {
      return foundCf.label;
    }
  }

  // Fallback to capitalizing camelCase: e.g. "ticketNo" -> "Ticket No", "createdAt" -> "Created At"
  const spaced = col
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
  return spaced;
};

const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  advanceFilterFields,
  setAdvanceFilter,
  advanceFilters,
  resetPagination,
  columns,
  customFields,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslate('translation');

  const defaultColumn = advanceFilterFields?.[0] || '';

  const [filterValue, setFilterValue] = React.useState<any[]>([]);
  const [filters, setFilters] = React.useState<Filter[]>([
    { column: defaultColumn, operator: 'contains', value: '' },
  ]);

  const {
    _recordList,
    isFetching,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetLabels();

  // ✅ Update a single filter
  const updateFilter = (index: number, key: keyof Filter, value: string) => {
    const updated = [...filters];
    updated[index][key] = value;
    setFilters(updated);
  };

  // ✅ Add a new filter
  const addFilter = () => {
    setFilters([...filters, { column: defaultColumn, operator: 'contains', value: '' }]);
    setFilterValue((prev) => [...prev, '']);
  };

  // ✅ Remove a single filter
  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
    setFilterValue(filterValue.filter((_, i) => i !== index));
  };

  // ✅ Remove all filters
  const removeAll = () => {
    setFilters([]);
    setFilterValue([]);
  };

  // ✅ Apply filters
  const handleAdvanceFilterSubmit = () => {
    const updatedFilters = filters.map((filter) => {
      if (filter.column === 'Phone' && filter?.value?.startsWith('0')) {
        return { ...filter, value: '966' + filter.value.slice(1) };
      }
      return filter;
    });
    setAdvanceFilter(updatedFilters);
    onClose();
    resetPagination?.();
  };

  // ✅ Manage open/close state for label fetching
  React.useEffect(() => {
    if (open) {
      openListHandler?.();

      // Only reset filters if parent sent new data (including empty list for reset)
      if (advanceFilters) {
        if (advanceFilters.length > 0) {
          setFilters(advanceFilters);
        } else {
          // Reset to default empty row if cleared externally
          setFilters([{ column: defaultColumn, operator: 'contains', value: '' }]);
        }
      }
    } else {
      closeListHandler?.();
    }
  }, [open, advanceFilters, defaultColumn]);

  // ✅ Keep label objects in sync with filters
  React.useEffect(() => {
    if (!open) return;

    if (filters.length === 0) {
      setFilterValue([]);
      return;
    }

    setFilterValue(
      filters.map((f) => {
        if (f.column === 'Label name') {
          const found = _recordList.find((x: any) => x.labelName === f.value);
          return found || (f.value ? { labelName: f.value, labelColor: '#ccc' } : null);
        }
        return f.value || '';
      })
    );
  }, [filters, _recordList, open]);

  const isFieldWithOutValue = ['is empty', 'is not empty'];
  const isApplyDisabled = filters.some(
    (filter) => !isFieldWithOutValue.includes(filter.operator) && filter.value.trim() === ''
  );

  // ✅ Manage Autocomplete open state per filter
  const [openStates, setOpenStates] = React.useState<Record<number, boolean>>({});

  const handleAutocompleteOpen = (index: number) => {
    openListHandler?.();
    setOpenStates((s) => ({ ...s, [index]: true }));
  };

  const handleAutocompleteClose = (index: number) => {
    closeListHandler?.();
    setOpenStates((s) => ({ ...s, [index]: false }));
  };

  const isRtl = theme.direction === 'rtl';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: isRtl ? 'right' : 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: isRtl ? 'right' : 'left',
      }}
      PaperProps={{
        sx: {
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: 1.5,
          boxShadow: '0 12px 32px -4px rgba(0, 0, 0, 0.16)',
        },
      }}
    >
      <Box
        p={2}
        sx={{
          width: isMobile ? 300 : 600,
          maxWidth: '100%',
          maxHeight: isMobile ? '70vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible',
          boxSizing: 'border-box',
        }}
      >
        {filters.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={60} mb={1}>
            <Typography variant="body2" color="text.secondary">
              {t('ADD_FILTER')}
            </Typography>
          </Box>
        ) : (
          filters.map((filter, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                mb: 2,
                ...(isMobile
                  ? {
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      p: 2,
                    }
                  : {
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      alignItems: 'center',
                    }),
              }}
            >
              {isMobile && (
                <IconButton
                  onClick={() => removeFilter(index)}
                  size="small"
                  disabled={filters.length < 2}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -5,
                    zIndex: 1,
                  }}
                >
                  <Iconify icon="mdi:close" />
                </IconButton>
              )}

              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: isMobile ? 'flex-start' : 'space-between',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1.5 : 1,
                  alignItems: 'center',
                }}
              >
                {/* Column */}
                <Select
                  sx={{
                    width: isMobile ? '100%' : '30%',
                    flex: isMobile ? 'none' : '1 1 auto',
                    minWidth: isMobile ? 'auto' : 130,
                  }}
                  value={filter.column}
                  onChange={(e: SelectChangeEvent<string>) =>
                    updateFilter(index, 'column', e.target.value)
                  }
                  size="small"
                >
                  {advanceFilterFields.map((col: any) => (
                    <MenuItem key={col} value={col}>
                      {getFieldLabel(col, t, columns, customFields)}
                    </MenuItem>
                  ))}
                </Select>

                {/* Operator */}
                <Select
                  sx={{
                    width: isMobile ? '100%' : '30%',
                    flex: isMobile ? 'none' : '1 1 auto',
                    minWidth: isMobile ? 'auto' : 130,
                  }}
                  value={filter.operator}
                  onChange={(e: SelectChangeEvent<string>) =>
                    updateFilter(index, 'operator', e.target.value)
                  }
                  size="small"
                >
                  {operators.map((op) => (
                    <MenuItem
                      key={op}
                      value={op}
                      disabled={filter.column === 'Label name' && isFieldWithOutValue.includes(op)}
                    >
                      {t(op.toUpperCase().replace(/ /g, '_'))}
                    </MenuItem>
                  ))}
                </Select>

                {/* Value */}
                {!isFieldWithOutValue.includes(filter.operator) &&
                  (filter.column === 'Label name' ? (
                    <Autocomplete
                      disableClearable
                      open={!!openStates[index]}
                      onOpen={() => handleAutocompleteOpen(index)}
                      onClose={() => handleAutocompleteClose(index)}
                      options={_recordList}
                      value={filterValue[index] || null}
                      loading={isFetching}
                      onChange={(e, newValue) => {
                        setFilterValue((prev: any[]) => {
                          const updated = [...prev];
                          updated[index] = newValue;
                          return updated;
                        });
                        updateFilter(index, 'value', newValue?.labelName || '');
                      }}
                      onInputChange={(e, val, reason) => {
                        if (reason === 'input') searchValueHandler(val);
                      }}
                      slotProps={{
                        listbox: {
                          onScroll: listScrollHandler,
                          sx: { maxHeight: 200, overflowY: 'auto' },
                        },
                      }}
                      getOptionLabel={(option: any) => option?.labelName || ''}
                      isOptionEqualToValue={(a: any, b: any) => a?.labelName === b?.labelName}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={t('LABELS')}
                          placeholder={t('SEARCH')}
                        />
                      )}
                      renderOption={(props, option) => (
                        <MenuItem
                          {...props}
                          key={option?.id}
                          disabled={option?.id === 'no-label'}
                          style={{
                            backgroundColor: option?.labelColor,
                            color: getContrastYIQ(option?.labelColor),
                            padding: '8px',
                          }}
                        >
                          {option?.labelName}
                        </MenuItem>
                      )}
                      sx={{
                        width: isMobile ? '100%' : '30%',
                        flex: isMobile ? 'none' : '1 1 auto',
                        minWidth: isMobile ? 'auto' : 130,
                      }}
                    />
                  ) : (
                    <TextField
                      value={filter.value}
                      onChange={(e) => updateFilter(index, 'value', e.target.value)}
                      size="small"
                      placeholder={t('FILTER_VALUE')}
                      sx={{
                        width: isMobile ? '100%' : '30%',
                        flex: isMobile ? 'none' : '1 1 auto',
                        minWidth: isMobile ? 'auto' : 130,
                      }}
                    />
                  ))}
              </Box>

              {!isMobile && (
                <IconButton
                  onClick={() => removeFilter(index)}
                  size="small"
                  disabled={filters.length < 2}
                  sx={{ flexShrink: 0 }}
                >
                  <Iconify icon="mdi:close" />
                </IconButton>
              )}
            </Box>
          ))
        )}

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box>
            <Button onClick={addFilter}>
              <Iconify icon="mdi:filter-plus" />
              {t('ADD_FILTER')}
            </Button>
            <Button onClick={removeAll}>
              <Iconify icon="mdi:filter-off" />
              {t('REMOVE_ALL')}
            </Button>
          </Box>
          <Button color="error" onClick={handleAdvanceFilterSubmit} disabled={isApplyDisabled}>
            {t('APPLY')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterPopover;
